import React, { useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Textarea } from "../../components/ui/textarea";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { 
  Settings, Save, RefreshCw, Mail, Phone, Globe, 
  Shield, Database, Bell, Zap, AlertTriangle, DollarSign, MessageSquare, PhoneCall
} from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

const AdminSettings = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingEmail, setTestingEmail] = useState(false);
  const [emailDiagnostics, setEmailDiagnostics] = useState(null);
  const [settings, setSettings] = useState({
    // General
    app_name: "Fieldz AI",
    support_email: "support@fieldzai.com",
    maintenance_mode: false,
    
    // Email
    email_provider: "mailersend",
    email_from_address: "noreply@fieldzai.com",
    email_from_name: "Fieldz AI",
    
    // Twilio
    twilio_enabled: false,
    twilio_webhook_base: "",
    
    // Limits
    default_plan: "standard",
    trial_days: 14,
    
    // Notifications
    notify_new_signups: true,
    notify_subscription_changes: true,
    notify_usage_alerts: true,
    usage_alert_threshold: 80,
    
    // Pricing
    phone_number_price: 5.00,
    sms_price_per_unit: 0.05,
    additional_sms_bundle_size: 100,
    additional_sms_bundle_price: 4.00,
    minutes_price_per_unit: 0.03,
    additional_minutes_bundle_size: 100,
    additional_minutes_bundle_price: 2.50,
    overage_sms_price: 0.06,
    overage_minutes_price: 0.04
  });

  const getAuthConfig = () => {
    const token = localStorage.getItem("token");
    return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await axios.get(`${API}/admin/settings`, getAuthConfig());
      setSettings(prev => ({ ...prev, ...res.data }));
    } catch (error) {
      // Settings might not exist yet, use defaults
      console.log("Using default settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/settings`, settings, getAuthConfig());
      toast.success("Settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const testEmailDelivery = async () => {
    setTestingEmail(true);
    setEmailDiagnostics(null);
    try {
      const res = await axios.post(`${API}/admin/email/test`, {}, getAuthConfig());
      setEmailDiagnostics(res.data);
      if (res.data.success) {
        toast.success(`Test email sent to ${res.data.to_email}. Check your inbox!`);
      } else {
        toast.error("Email test failed. See diagnostics below.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to run email test");
      setEmailDiagnostics({ success: false, error: error.message });
    } finally {
      setTestingEmail(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-muted-foreground">Loading settings...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-['Manrope']">System Settings</h1>
          <p className="text-muted-foreground">Configure global application settings</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchSettings} className="border-theme text-foreground">
            <RefreshCw className="w-4 h-4 mr-2" /> Reset
          </Button>
          <Button onClick={saveSettings} disabled={saving} className="bg-[#0046BE] text-white">
            <Save className="w-4 h-4 mr-2" /> {saving ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </div>

      <Tabs defaultValue="general" className="space-y-6">
        <TabsList className="bg-theme-surface border border-theme flex-wrap">
          <TabsTrigger value="general" className="data-[state=active]:bg-muted text-foreground">
            <Settings className="w-4 h-4 mr-2" /> General
          </TabsTrigger>
          <TabsTrigger value="pricing" className="data-[state=active]:bg-muted text-foreground">
            <DollarSign className="w-4 h-4 mr-2" /> Pricing
          </TabsTrigger>
          <TabsTrigger value="email" className="data-[state=active]:bg-muted text-foreground">
            <Mail className="w-4 h-4 mr-2" /> Email
          </TabsTrigger>
          <TabsTrigger value="integrations" className="data-[state=active]:bg-muted text-foreground">
            <Zap className="w-4 h-4 mr-2" /> Integrations
          </TabsTrigger>
          <TabsTrigger value="notifications" className="data-[state=active]:bg-muted text-foreground">
            <Bell className="w-4 h-4 mr-2" /> Notifications
          </TabsTrigger>
        </TabsList>

        {/* General Settings */}
        <TabsContent value="general">
          <Card className="glass-card border-theme">
            <CardHeader>
              <CardTitle className="text-foreground">General Settings</CardTitle>
              <CardDescription className="text-muted-foreground">
                Basic application configuration
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-muted-foreground">Application Name</Label>
                  <Input
                    value={settings.app_name}
                    onChange={(e) => updateSetting("app_name", e.target.value)}
                    className="bg-theme-surface border-theme text-foreground mt-1"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground">Support Email</Label>
                  <Input
                    value={settings.support_email}
                    onChange={(e) => updateSetting("support_email", e.target.value)}
                    className="bg-theme-surface border-theme text-foreground mt-1"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-muted-foreground">Default Plan for New Users</Label>
                  <Input
                    value={settings.default_plan}
                    onChange={(e) => updateSetting("default_plan", e.target.value)}
                    className="bg-theme-surface border-theme text-foreground mt-1"
                    placeholder="standard"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground">Trial Period (Days)</Label>
                  <Input
                    type="number"
                    value={settings.trial_days}
                    onChange={(e) => updateSetting("trial_days", parseInt(e.target.value) || 0)}
                    className="bg-theme-surface border-theme text-foreground mt-1"
                  />
                </div>
              </div>

              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="w-5 h-5 text-yellow-400" />
                    <div>
                      <p className="text-foreground font-medium">Maintenance Mode</p>
                      <p className="text-muted-foreground text-sm">When enabled, only admins can access the app</p>
                    </div>
                  </div>
                  <Switch
                    checked={settings.maintenance_mode}
                    onCheckedChange={(checked) => updateSetting("maintenance_mode", checked)}
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Settings */}
        <TabsContent value="pricing">
          <div className="space-y-6">
            {/* Phone Number Pricing */}
            <Card className="glass-card border-theme">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <Phone className="w-5 h-5 text-cyan-400" />
                  <CardTitle className="text-foreground">Phone Number Pricing</CardTitle>
                </div>
                <CardDescription className="text-muted-foreground">
                  Set prices for purchasing and maintaining phone numbers
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 gap-6">
                  <div>
                    <Label className="text-muted-foreground">Phone Number Price (One-time Purchase)</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.phone_number_price}
                        onChange={(e) => updateSetting("phone_number_price", parseFloat(e.target.value) || 0)}
                        className="bg-theme-surface border-theme text-foreground pl-7"
                        data-testid="phone-number-price"
                      />
                    </div>
                    <p className="text-muted-foreground text-xs mt-1">Fixed one-time fee charged when user purchases a new phone number</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* SMS Pricing */}
            <Card className="glass-card border-theme">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <MessageSquare className="w-5 h-5 text-green-400" />
                  <CardTitle className="text-foreground">SMS Pricing</CardTitle>
                </div>
                <CardDescription className="text-muted-foreground">
                  Configure SMS pricing and bundles for additional messages
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <Label className="text-muted-foreground">Price per SMS</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.sms_price_per_unit}
                        onChange={(e) => updateSetting("sms_price_per_unit", parseFloat(e.target.value) || 0)}
                        className="bg-theme-surface border-theme text-foreground pl-7"
                        data-testid="sms-price-per-unit"
                      />
                    </div>
                    <p className="text-muted-foreground text-xs mt-1">Base price per SMS message</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Additional Bundle Size</Label>
                    <Input
                      type="number"
                      value={settings.additional_sms_bundle_size}
                      onChange={(e) => updateSetting("additional_sms_bundle_size", parseInt(e.target.value) || 0)}
                      className="bg-theme-surface border-theme text-foreground mt-1"
                      data-testid="sms-bundle-size"
                    />
                    <p className="text-muted-foreground text-xs mt-1">Number of SMS in each bundle</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Bundle Price</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.additional_sms_bundle_price}
                        onChange={(e) => updateSetting("additional_sms_bundle_price", parseFloat(e.target.value) || 0)}
                        className="bg-theme-surface border-theme text-foreground pl-7"
                        data-testid="sms-bundle-price"
                      />
                    </div>
                    <p className="text-muted-foreground text-xs mt-1">Price for additional SMS bundle</p>
                  </div>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-foreground font-medium">Overage Rate</p>
                      <p className="text-muted-foreground text-sm">Price per SMS when user exceeds their plan limit without purchasing bundles</p>
                    </div>
                    <div className="relative w-32">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.overage_sms_price}
                        onChange={(e) => updateSetting("overage_sms_price", parseFloat(e.target.value) || 0)}
                        className="bg-theme-surface border-theme text-foreground pl-7"
                        data-testid="sms-overage-price"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Voice Minutes Pricing */}
            <Card className="glass-card border-theme">
              <CardHeader>
                <div className="flex items-center gap-2">
                  <PhoneCall className="w-5 h-5 text-purple-400" />
                  <CardTitle className="text-foreground">Voice Minutes Pricing</CardTitle>
                </div>
                <CardDescription className="text-muted-foreground">
                  Configure voice call pricing and bundles for additional minutes
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <Label className="text-muted-foreground">Price per Minute</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.minutes_price_per_unit}
                        onChange={(e) => updateSetting("minutes_price_per_unit", parseFloat(e.target.value) || 0)}
                        className="bg-theme-surface border-theme text-foreground pl-7"
                        data-testid="minutes-price-per-unit"
                      />
                    </div>
                    <p className="text-muted-foreground text-xs mt-1">Base price per voice minute</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Additional Bundle Size</Label>
                    <Input
                      type="number"
                      value={settings.additional_minutes_bundle_size}
                      onChange={(e) => updateSetting("additional_minutes_bundle_size", parseInt(e.target.value) || 0)}
                      className="bg-theme-surface border-theme text-foreground mt-1"
                      data-testid="minutes-bundle-size"
                    />
                    <p className="text-muted-foreground text-xs mt-1">Number of minutes in each bundle</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Bundle Price</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.additional_minutes_bundle_price}
                        onChange={(e) => updateSetting("additional_minutes_bundle_price", parseFloat(e.target.value) || 0)}
                        className="bg-theme-surface border-theme text-foreground pl-7"
                        data-testid="minutes-bundle-price"
                      />
                    </div>
                    <p className="text-muted-foreground text-xs mt-1">Price for additional minutes bundle</p>
                  </div>
                </div>
                <div className="bg-muted/50 rounded-lg p-4 mt-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-foreground font-medium">Overage Rate</p>
                      <p className="text-muted-foreground text-sm">Price per minute when user exceeds their plan limit without purchasing bundles</p>
                    </div>
                    <div className="relative w-32">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                      <Input
                        type="number"
                        step="0.01"
                        value={settings.overage_minutes_price}
                        onChange={(e) => updateSetting("overage_minutes_price", parseFloat(e.target.value) || 0)}
                        className="bg-theme-surface border-theme text-foreground pl-7"
                        data-testid="minutes-overage-price"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Pricing Summary */}
            <Card className="glass-card border-[#FFCC00]/30 bg-[#FFCC00]/5">
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <DollarSign className="w-5 h-5 text-[#FFCC00] mt-0.5" />
                  <div>
                    <p className="text-foreground font-medium">Pricing Summary</p>
                    <div className="text-muted-foreground text-sm mt-2 space-y-1">
                      <p>• Phone number: ${settings.phone_number_price} (one-time purchase)</p>
                      <p>• SMS bundle: {settings.additional_sms_bundle_size} messages for ${settings.additional_sms_bundle_price} (${(settings.additional_sms_bundle_price / settings.additional_sms_bundle_size).toFixed(3)}/SMS)</p>
                      <p>• Minutes bundle: {settings.additional_minutes_bundle_size} min for ${settings.additional_minutes_bundle_price} (${(settings.additional_minutes_bundle_price / settings.additional_minutes_bundle_size).toFixed(3)}/min)</p>
                      <p>• Overage rates: ${settings.overage_sms_price}/SMS, ${settings.overage_minutes_price}/min</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Email Settings */}
        <TabsContent value="email">
          <Card className="glass-card border-theme">
            <CardHeader>
              <CardTitle className="text-foreground">Email Configuration</CardTitle>
              <CardDescription className="text-muted-foreground">
                Configure email sending settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <Label className="text-muted-foreground">Email Provider</Label>
                  <Input
                    value={settings.email_provider}
                    onChange={(e) => updateSetting("email_provider", e.target.value)}
                    className="bg-theme-surface border-theme text-foreground mt-1"
                    placeholder="mailersend"
                  />
                </div>
                <div>
                  <Label className="text-muted-foreground">From Address</Label>
                  <Input
                    value={settings.email_from_address}
                    onChange={(e) => updateSetting("email_from_address", e.target.value)}
                    className="bg-theme-surface border-theme text-foreground mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="text-muted-foreground">From Name</Label>
                <Input
                  value={settings.email_from_name}
                  onChange={(e) => updateSetting("email_from_name", e.target.value)}
                  className="bg-theme-surface border-theme text-foreground mt-1"
                />
              </div>
              
              <div className="bg-muted/50 rounded-lg p-4">
                <p className="text-muted-foreground text-sm">
                  Email API keys are configured via environment variables for security.
                  Contact your administrator to update MAILERSEND_API_KEY.
                </p>
              </div>

              {/* Test Email Button */}
              <div className="border border-border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-foreground font-medium">Test Email Delivery</p>
                    <p className="text-muted-foreground text-sm">Send a test email to your admin address to verify delivery works</p>
                  </div>
                  <Button
                    onClick={testEmailDelivery}
                    disabled={testingEmail}
                    className="bg-[#0046BE] hover:bg-[#003da6] text-white"
                    data-testid="test-email-btn"
                  >
                    {testingEmail ? (
                      <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Testing...</>
                    ) : (
                      <><Mail className="w-4 h-4 mr-2" /> Send Test Email</>
                    )}
                  </Button>
                </div>

                {emailDiagnostics && (
                  <div className={`rounded-lg p-4 text-sm space-y-2 ${emailDiagnostics.success ? "bg-emerald-500/10 border border-emerald-500/30" : "bg-red-500/10 border border-red-500/30"}`} data-testid="email-diagnostics">
                    <p className={`font-medium ${emailDiagnostics.success ? "text-emerald-400" : "text-red-400"}`}>
                      {emailDiagnostics.success ? "Email sent successfully!" : "Email delivery failed"}
                    </p>
                    
                    {emailDiagnostics.diagnostics && (
                      <div className="space-y-2 text-xs font-mono">
                        {/* Env Check */}
                        <div>
                          <p className="text-muted-foreground font-sans font-medium mb-1">Environment:</p>
                          <p className="text-foreground">
                            API Key (active): {emailDiagnostics.diagnostics.env_check?.api_key_present ? (
                              <span className="text-emerald-400">{emailDiagnostics.diagnostics.env_check.api_key_prefix}</span>
                            ) : (
                              <span className="text-red-400">MISSING</span>
                            )}
                          </p>
                          <p className="text-foreground">From: {emailDiagnostics.diagnostics.env_check?.from_email || "MISSING"}</p>
                          {emailDiagnostics.diagnostics.env_check?.env_file_exists !== undefined && (
                            <>
                              <p className="text-foreground mt-1">
                                .env file: {emailDiagnostics.diagnostics.env_check.env_file_exists ? (
                                  <span className="text-emerald-400">Found</span>
                                ) : (
                                  <span className="text-red-400">NOT FOUND</span>
                                )}
                                {emailDiagnostics.diagnostics.env_check.env_file_has_key && (
                                  <span className="ml-2">Key: <span className="text-emerald-400">{emailDiagnostics.diagnostics.env_check.env_file_key_prefix}</span></span>
                                )}
                              </p>
                              <p className="text-foreground">
                                System env key: <span className="text-muted-foreground">{emailDiagnostics.diagnostics.env_check.system_env_key_prefix}</span>
                              </p>
                            </>
                          )}
                        </div>

                        {/* API Connectivity */}
                        {emailDiagnostics.diagnostics.api_connectivity && (
                          <div>
                            <p className="text-muted-foreground font-sans font-medium mb-1">API Connectivity:</p>
                            <p className="text-foreground">
                              Status: <span className={emailDiagnostics.diagnostics.api_connectivity.status === "OK" ? "text-emerald-400" : "text-red-400"}>
                                {emailDiagnostics.diagnostics.api_connectivity.status}
                              </span>
                              {emailDiagnostics.diagnostics.api_connectivity.remaining !== undefined && (
                                <span className="ml-2">Quota: {emailDiagnostics.diagnostics.api_connectivity.remaining}/{emailDiagnostics.diagnostics.api_connectivity.quota} remaining</span>
                              )}
                            </p>
                          </div>
                        )}

                        {/* Domain Status */}
                        {emailDiagnostics.diagnostics.domain_status && (
                          <div>
                            <p className="text-muted-foreground font-sans font-medium mb-1">Domain:</p>
                            <p className="text-foreground">
                              {emailDiagnostics.diagnostics.domain_status.name} — 
                              Verified: <span className={emailDiagnostics.diagnostics.domain_status.is_verified ? "text-emerald-400" : "text-red-400"}>
                                {emailDiagnostics.diagnostics.domain_status.is_verified ? "Yes" : "No"}
                              </span>,
                              DKIM: <span className={emailDiagnostics.diagnostics.domain_status.dkim ? "text-emerald-400" : "text-red-400"}>
                                {emailDiagnostics.diagnostics.domain_status.dkim ? "Yes" : "No"}
                              </span>,
                              SPF: <span className={emailDiagnostics.diagnostics.domain_status.spf ? "text-emerald-400" : "text-red-400"}>
                                {emailDiagnostics.diagnostics.domain_status.spf ? "Yes" : "No"}
                              </span>
                            </p>
                          </div>
                        )}

                        {/* Send Result */}
                        {emailDiagnostics.diagnostics.send_result?.error && (
                          <p className="text-red-400">Error: {emailDiagnostics.diagnostics.send_result.error}</p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Integrations */}
        <TabsContent value="integrations">
          <Card className="glass-card border-theme">
            <CardHeader>
              <CardTitle className="text-foreground">Integrations</CardTitle>
              <CardDescription className="text-muted-foreground">
                Configure third-party service integrations
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Twilio */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 text-cyan-400" />
                    <div>
                      <p className="text-foreground font-medium">Twilio</p>
                      <p className="text-muted-foreground text-sm">Voice & SMS integration</p>
                    </div>
                  </div>
                  <Badge className={settings.twilio_enabled ? "bg-green-500/20 text-green-400" : "bg-gray-500/20 text-gray-400"}>
                    {settings.twilio_enabled ? "Enabled" : "Disabled"}
                  </Badge>
                </div>
                <div>
                  <Label className="text-muted-foreground">Webhook Base URL</Label>
                  <Input
                    value={settings.twilio_webhook_base}
                    onChange={(e) => updateSetting("twilio_webhook_base", e.target.value)}
                    className="bg-theme-surface border-theme text-foreground mt-1"
                    placeholder="https://your-domain.com/api/twilio"
                  />
                  <p className="text-muted-foreground text-xs mt-1">
                    Used for Twilio webhook callbacks
                  </p>
                </div>
              </div>

              {/* Google Places */}
              <div className="bg-muted/50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Globe className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-foreground font-medium">Google Places API</p>
                      <p className="text-muted-foreground text-sm">Address autocomplete</p>
                    </div>
                  </div>
                  <Badge className="bg-green-500/20 text-green-400">Configured via ENV</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications */}
        <TabsContent value="notifications">
          <Card className="glass-card border-theme">
            <CardHeader>
              <CardTitle className="text-foreground">Admin Notifications</CardTitle>
              <CardDescription className="text-muted-foreground">
                Configure when you receive admin alerts
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between bg-muted/50 rounded-lg p-4">
                <div>
                  <p className="text-foreground font-medium">New User Signups</p>
                  <p className="text-muted-foreground text-sm">Get notified when new users register</p>
                </div>
                <Switch
                  checked={settings.notify_new_signups}
                  onCheckedChange={(checked) => updateSetting("notify_new_signups", checked)}
                />
              </div>

              <div className="flex items-center justify-between bg-muted/50 rounded-lg p-4">
                <div>
                  <p className="text-foreground font-medium">Subscription Changes</p>
                  <p className="text-muted-foreground text-sm">Get notified when users upgrade/downgrade plans</p>
                </div>
                <Switch
                  checked={settings.notify_subscription_changes}
                  onCheckedChange={(checked) => updateSetting("notify_subscription_changes", checked)}
                />
              </div>

              <div className="flex items-center justify-between bg-muted/50 rounded-lg p-4">
                <div>
                  <p className="text-foreground font-medium">Usage Alerts</p>
                  <p className="text-muted-foreground text-sm">Get notified when users approach limits</p>
                </div>
                <Switch
                  checked={settings.notify_usage_alerts}
                  onCheckedChange={(checked) => updateSetting("notify_usage_alerts", checked)}
                />
              </div>

              {settings.notify_usage_alerts && (
                <div className="ml-4">
                  <Label className="text-muted-foreground">Alert Threshold (%)</Label>
                  <Input
                    type="number"
                    value={settings.usage_alert_threshold}
                    onChange={(e) => updateSetting("usage_alert_threshold", parseInt(e.target.value) || 80)}
                    className="bg-theme-surface border-theme text-foreground mt-1 w-32"
                    min={50}
                    max={100}
                  />
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSettings;
